import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Container, Row, Col, Card, CardBody, Form, FormGroup, Label, Input, Button } from "reactstrap"
import FeatherIcon from "feather-icons-react"
import { CONTACT_INFO } from "../../constants"

const ContactPage = () => {
    const { t } = useTranslation()

    // Scroll to top on page load
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const contactInfo = [
        {
            icon: "map-pin",
            title: t("contact.info.address"),
            value: CONTACT_INFO.address
        },
        {
            icon: "phone",
            title: t("contact.info.phone"),
            value: CONTACT_INFO.phone
        },
        {
            icon: "mail",
            title: t("contact.info.email"),
            value: CONTACT_INFO.email
        }
    ]

    return (
        <React.Fragment>
            {/* Hero Section */}
            <section
                className="bg-half-170 d-table w-100"
                style={{
                    background: "linear-gradient(135deg, #1E3A5F 0%, #0D1F33 100%)"
                }}
            >
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8} className="text-center">
                            <div className="title-heading">
                                <h1 className="heading text-white mb-3">
                                    {t("contact.title")}
                                </h1>
                                <p className="text-white-50">
                                    {t("contact.subtitle")}
                                </p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Contact Content */}
            <section className="section">
                <Container>
                    <Row>
                        {/* Contact Info Cards */}
                        <Col lg={4}>
                            <div className="mb-4">
                                {contactInfo.map((info, key) => (
                                    <Card key={key} className="border-0 shadow rounded mb-3">
                                        <CardBody className="p-4">
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="rounded-circle bg-soft-primary d-flex align-items-center justify-content-center me-3"
                                                    style={{ width: "50px", height: "50px", backgroundColor: "rgba(230, 81, 0, 0.1)" }}
                                                >
                                                    <FeatherIcon
                                                        icon={info.icon}
                                                        className="text-primary"
                                                        style={{ width: "20px", height: "20px" }}
                                                    />
                                                </div>
                                                <div>
                                                    <h6 className="text-muted mb-1">{info.title}</h6>
                                                    <p className="text-dark mb-0 fw-medium">{info.value}</p>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                ))}
                            </div>

                            {/* Working Hours */}
                            <Card className="border-0 shadow rounded">
                                <CardBody className="p-4">
                                    <h5 className="mb-3">{t("contact.workingHours")}</h5>
                                    <ul className="list-unstyled mb-0">
                                        <li className="d-flex justify-content-between py-2 border-bottom">
                                            <span className="text-muted">{t("contact.weekdays")}</span>
                                            <span className="fw-medium">{CONTACT_INFO.workingHours.weekdays}</span>
                                        </li>
                                        <li className="d-flex justify-content-between py-2 border-bottom">
                                            <span className="text-muted">{t("contact.saturday")}</span>
                                            <span className="fw-medium">{CONTACT_INFO.workingHours.saturday}</span>
                                        </li>
                                        <li className="d-flex justify-content-between py-2">
                                            <span className="text-muted">{t("contact.sunday")}</span>
                                            <span className="fw-medium">{t("contact.closed")}</span>
                                        </li>
                                    </ul>
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Contact Form */}
                        <Col lg={8} className="mt-4 mt-lg-0">
                            <Card className="border-0 shadow rounded">
                                <CardBody className="p-5">
                                    <h4 className="mb-4">{t("contact.formTitle")}</h4>
                                    <p className="text-muted mb-4">
                                        {t("contact.formDescription")}
                                    </p>

                                    <Form>
                                        <Row>
                                            <Col md={6}>
                                                <FormGroup className="mb-3">
                                                    <Label className="form-label">{t("contact.form.name")} *</Label>
                                                    <Input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder={t("contact.form.namePlaceholder")}
                                                        required
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup className="mb-3">
                                                    <Label className="form-label">{t("contact.form.email")} *</Label>
                                                    <Input
                                                        type="email"
                                                        className="form-control"
                                                        placeholder={t("contact.form.emailPlaceholder")}
                                                        required
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup className="mb-3">
                                                    <Label className="form-label">{t("contact.form.phone")}</Label>
                                                    <Input
                                                        type="tel"
                                                        className="form-control"
                                                        placeholder={t("contact.form.phonePlaceholder")}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup className="mb-3">
                                                    <Label className="form-label">{t("contact.form.company")}</Label>
                                                    <Input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder={t("contact.form.companyPlaceholder")}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col xs={12}>
                                                <FormGroup className="mb-3">
                                                    <Label className="form-label">{t("contact.form.subject")} *</Label>
                                                    <Input
                                                        type="select"
                                                        className="form-control"
                                                        required
                                                    >
                                                        <option value="">{t("contact.form.subjectPlaceholder")}</option>
                                                        <option value="demo">{t("contact.form.subjectOptions.demo")}</option>
                                                        <option value="price">{t("contact.form.subjectOptions.price")}</option>
                                                        <option value="support">{t("contact.form.subjectOptions.support")}</option>
                                                        <option value="partnership">{t("contact.form.subjectOptions.partnership")}</option>
                                                        <option value="other">{t("contact.form.subjectOptions.other")}</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                            <Col xs={12}>
                                                <FormGroup className="mb-3">
                                                    <Label className="form-label">{t("contact.form.message")} *</Label>
                                                    <Input
                                                        type="textarea"
                                                        className="form-control"
                                                        rows="5"
                                                        placeholder={t("contact.form.messagePlaceholder")}
                                                        required
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col xs={12}>
                                                <Button color="primary" className="w-100">
                                                    <FeatherIcon icon="send" className="fea icon-sm me-2" />
                                                    {t("contact.form.submit")}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>
        </React.Fragment>
    )
}

export default ContactPage
